import { handleError } from "@ch/helpers/utils/handleError";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiWrapper } from ".";

type ServiceFunction<A, B> = (payload: A) => Promise<B>;

interface UseGetRequestOptions<A, B> {
  service: ServiceFunction<A, B>;
  payload?: A;
  onError?: (val?: any) => void;
  /** Single tag or list (mobile-style multi-invalidate). */
  tag: string | string[];
  showNotification?: boolean;
}

interface UseGetRequestResult<B> {
  loading: boolean;
  isLoading: boolean;
  data?: B;
  error?: any;
}

export function useMutateRequest<A, B>({
  service,
  tag,
  onSuccess,
  onError,
  invalidate = true,
  showNotification = true,
}: UseGetRequestOptions<A, B> & {
  invalidate?: boolean;
  onSuccess?: (val?: B) => void;
}): UseGetRequestResult<B> & {
  trigger: (payload: A) => void;
  isMutating?: boolean;
} {
  const queryClient = useQueryClient();

  const info = useMutation({
    mutationFn: (payload: A) => {
      return apiWrapper(() => service(payload));
    },
    onSuccess: (data) => {
      if (onSuccess) {
        onSuccess(data);
      }
      if (invalidate) {
        const tags = Array.isArray(tag) ? tag : [tag];
        tags.forEach((key) => {
          void queryClient.invalidateQueries({
            queryKey: [key],
            type: "all",
          });
        });
      }
    },
    onError: (error: any) => {
      const errorData = handleError(error, undefined, showNotification);
      onError?.(errorData?.message || "Unknown error");
    },
  });

  return {
    loading: info.isPending,
    isLoading: info.isPending,
    isMutating: info.isPending,
    data: info.data,
    trigger: info.mutate,
    error: info.error,
  };
}
